'use client';

import { Autocomplete, TextField, InputAdornment, CircularProgress, Box } from '@mui/material';
import { SearchIcon } from '@/shared/ui/display/EmojiIcon';
import { useCallback } from 'react';

export interface ToolbarAutocompleteOption<T = any> {
  label: string;
  value: T;
  subtitle?: string;
}

interface ToolbarAutocompleteProps<T = any> {
  value: string;
  onChange: (value: string) => void;
  getOptions: (inputValue: string) => ToolbarAutocompleteOption<T>[];
  onSelect: (option: ToolbarAutocompleteOption<T> | null) => void;
  loading?: boolean;
  placeholder?: string;
  width?: number | string;
  noOptionsText?: string;
  children: (option: ToolbarAutocompleteOption<T>) => React.ReactNode;
}

export function ToolbarAutocomplete<T = any>({
  value,
  onChange,
  getOptions,
  onSelect,
  loading = false,
  placeholder = '검색...',
  width = 350,
  noOptionsText = '결과가 없습니다',
  children,
}: ToolbarAutocompleteProps<T>) {
  // inputValue가 변경될 때마다 getOptions를 호출하여 옵션 가져오기
  const options = getOptions(value);

  const handleInputChange = useCallback(
    (event: any, newValue: string) => {
      onChange(newValue);
    },
    [onChange]
  );

  const handleChange = useCallback(
    (event: any, newValue: ToolbarAutocompleteOption<T> | string | null) => {
      if (typeof newValue === 'string') {
        onSelect(null);
      } else {
        onSelect(newValue);
      }
    },
    [onSelect]
  );

  // paste 시 preventDefault 필요: 기본 paste를 막지 않으면 onChange + 브라우저 paste가 둘 다 실행되어 텍스트가 두 번 붙여넣기됨
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text').trim();
      if (!pastedText) return;
      const input = e.target as HTMLInputElement;
      const start = input.selectionStart ?? value.length;
      const end = input.selectionEnd ?? value.length;
      const newValue = value.slice(0, start) + pastedText + value.slice(end);
      onChange(newValue);
      e.preventDefault();
    },
    [value, onChange]
  );

  return (
    <Autocomplete
      freeSolo
      inputValue={value}
      onInputChange={handleInputChange}
      onChange={handleChange}
      options={options}
      // MUI의 기본 필터링은 하이픈(-) 같은 특수문자를 포함한 검색어에서 제대로 작동하지 않습니다.
      // getOptions에서 이미 필터링된 결과를 사용하므로 기본 필터링을 비활성화합니다.
      // commentBy: jeeinho 2026.02.07
      filterOptions={(options) => options}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.label;
      }}
      loading={loading}
      noOptionsText={noOptionsText}
      size="small"
      sx={{ width }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          inputProps={{
            ...params.inputProps,
            onPaste: handlePaste,
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '32px',
              fontSize: '0.875rem',
              paddingRight: '4px',
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          {children(option)}
        </Box>
      )}
    />
  );
}
